// Keyboard + mouse input with pointer lock. Exposes a pollable state that the
// game reads each frame; movement deltas and click edges are consumed on read.
// All listeners are removable via dispose() so a new game can start cleanly.

import { InputState } from './sim/player';

export class Input {
  private keys = new Set<string>();
  private mouseDX = 0;
  private mouseDY = 0;
  leftDown = false;
  rightDown = false;
  private leftClicks = 0;
  private rightClicks = 0;
  locked = false;

  onSelectSlot: ((index: number) => void) | null = null;
  onScroll: ((delta: number) => void) | null = null;
  onLockChange: ((locked: boolean) => void) | null = null;
  onDebugToggle: (() => void) | null = null;
  onInventoryToggle: (() => void) | null = null;

  private canvas: HTMLCanvasElement;
  private cleanups: (() => void)[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.on(canvas, 'click', () => {
      if (!this.locked) canvas.requestPointerLock();
    });
    this.on(document, 'pointerlockchange', () => {
      this.locked = document.pointerLockElement === canvas;
      if (!this.locked) this.keys.clear();
      this.onLockChange?.(this.locked);
    });
    this.on(window, 'keydown', (e: Event) => {
      const ev = e as KeyboardEvent;
      if (ev.code === 'F3') {
        ev.preventDefault();
        this.onDebugToggle?.();
        return;
      }
      if (ev.code === 'KeyE') {
        this.onInventoryToggle?.();
        return;
      }
      this.keys.add(ev.code);
      if (ev.code.startsWith('Digit')) {
        const n = Number(ev.code.slice(5));
        if (n >= 1 && n <= 9) this.onSelectSlot?.(n - 1);
      }
    });
    this.on(window, 'keyup', (e: Event) => this.keys.delete((e as KeyboardEvent).code));
    this.on(document, 'mousemove', (e: Event) => {
      if (!this.locked) return;
      const ev = e as MouseEvent;
      this.mouseDX += ev.movementX;
      this.mouseDY += ev.movementY;
    });
    this.on(canvas, 'mousedown', (e: Event) => {
      if (!this.locked) return;
      const ev = e as MouseEvent;
      if (ev.button === 0) {
        this.leftDown = true;
        this.leftClicks++;
      } else if (ev.button === 2) {
        this.rightDown = true;
        this.rightClicks++;
      }
    });
    this.on(window, 'mouseup', (e: Event) => {
      const ev = e as MouseEvent;
      if (ev.button === 0) this.leftDown = false;
      else if (ev.button === 2) this.rightDown = false;
    });
    this.on(canvas, 'contextmenu', (e: Event) => e.preventDefault());
    this.on(window, 'wheel', (e: Event) => {
      if (this.locked) this.onScroll?.((e as WheelEvent).deltaY);
    });
  }

  private on(target: EventTarget, type: string, handler: (e: Event) => void): void {
    target.addEventListener(type, handler);
    this.cleanups.push(() => target.removeEventListener(type, handler));
  }

  movementState(): InputState {
    return {
      forward: this.keys.has('KeyW'),
      back: this.keys.has('KeyS'),
      left: this.keys.has('KeyA'),
      right: this.keys.has('KeyD'),
      jump: this.keys.has('Space'),
    };
  }

  consumeMouseDelta(): { dx: number; dy: number } {
    const d = { dx: this.mouseDX, dy: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return d;
  }

  consumeLeftClicks(): number {
    const n = this.leftClicks;
    this.leftClicks = 0;
    return n;
  }

  consumeRightClicks(): number {
    const n = this.rightClicks;
    this.rightClicks = 0;
    return n;
  }

  requestLock(): void {
    if (!this.locked) this.canvas.requestPointerLock();
  }

  dispose(): void {
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups = [];
  }
}
